import { useState } from 'react';
import { outreach } from '@round/api';
import { lucideIcons, Tooltip } from '@round/creatorbase-ui';
import { OutreachResultsTable } from './components/OutreachResultsTable/OutreachResultsTable';
import { OutreachResultsTabs } from './components/OutreachResultsTabs/OutreachResultsTabs';
import { parseTab } from './components/OutreachResultsTabs/helpers';
import { OutreachResultsLocationFilter } from './components/OutreachResultsLocationFilter/OutreachResultsLocationFilter';
import { OutreachResultsEmptyState } from './components/OutreachResultsEmptyState/OutreachResultsEmptyState';
import { OutreachResultsBulkActions } from './components/OutreachResultsBulkActions/OutreachResultsBulkActions';
import CreatorDetailsModal from 'Modules/Plans/CreatorSearch/components/CreatorDetailsModal/CreatorDetailsModal';
import { getEmptyStateVariant } from './components/OutreachResultsEmptyState/helpers';
import { getHasActiveFilters, mapToApiParams, useOutreachResultsUrlState } from './hooks/useOutreachResultsUrlState';
import { useOutreachResults } from './hooks/useOutreachResults';
import { useOutreachResultsSelection } from './hooks/useOutreachResultsSelection';
import { getOrdering, getSorting } from './helpers';
import styles from './OutreachRequestResults.module.css';

const { TriangleAlert } = lucideIcons;

const LOAD_MORE_ERROR_TOOLTIP = 'Could not load more creators. Reload to try again.';

type Props = {
    request: outreach.OutreachRequest;
};

export const OutreachRequestResults = ({ request }: Props) => {
    const { urlState, setUrlState } = useOutreachResultsUrlState(request.filters);
    const {
        rows,
        isLoading,
        hasNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
        fetchNextPage,
        isError,
        errorMessage,
        getAvatarUrl,
        areAvatarsLoading,
    } = useOutreachResults({ outreach_request_id: request.id, ...mapToApiParams(urlState) });

    // The tab and the location decide which results are listed at all, so a selection made under one
    // has nothing to act on under the next. The ordering only rearranges the same rows.
    const { selectedIds, toggleResult, selectResults, clearSelection } = useOutreachResultsSelection(
        [request.id, urlState.tab, urlState.location].join(':')
    );

    const [expandedResultId, setExpandedResultId] = useState<number | null>(null);
    const [creatorDetailsAccountId, setCreatorDetailsAccountId] = useState<number | null>(null);

    return (
        <section className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Results</h2>

                {!isLoading && !isError && <span className={styles.count}>{request.results_count}</span>}

                {isFetchNextPageError && (
                    <Tooltip>
                        <Tooltip.Trigger asChild>
                            <span className={styles.loadMoreError} role="img" aria-label={LOAD_MORE_ERROR_TOOLTIP}>
                                <TriangleAlert />
                            </span>
                        </Tooltip.Trigger>
                        <Tooltip.Content>{LOAD_MORE_ERROR_TOOLTIP}</Tooltip.Content>
                    </Tooltip>
                )}
            </div>

            <div className={styles.controls}>
                <OutreachResultsTabs
                    request={request}
                    value={parseTab(urlState.tab)}
                    onChange={(tab) => {
                        setUrlState({ tab });
                        // A different tab is a different set of rows, so whichever one was expanded
                        // may no longer even be in the list.
                        setExpandedResultId(null);
                    }}
                />

                <div className={styles.controlsEnd}>
                    <OutreachResultsBulkActions
                        outreachRequestId={request.id}
                        rows={rows}
                        selectedIds={selectedIds}
                        onClearSelection={clearSelection}
                        getAvatarUrl={getAvatarUrl}
                        areAvatarsLoading={areAvatarsLoading}
                    />

                    <OutreachResultsLocationFilter
                        value={urlState.location}
                        onChange={(location) => setUrlState({ location })}
                    />
                </div>
            </div>

            <div className={styles.table}>
                <OutreachResultsTable
                    rows={rows}
                    meta={{
                        isLoading,
                        tab: parseTab(urlState.tab),
                        getAvatarUrl,
                        areAvatarsLoading,
                        selectedIds,
                        onToggleResult: toggleResult,
                        onSelectResults: selectResults,
                        onClearSelection: clearSelection,
                        onOpenCreatorDetails: setCreatorDetailsAccountId,
                    }}
                    columnVisibility={{
                        location: request.outputs.location,
                        email: request.outputs.email,
                        rate: request.outputs.rate,
                    }}
                    sorting={getSorting(urlState.ordering)}
                    onSortingChange={(sorting) => setUrlState({ ordering: getOrdering(sorting) })}
                    expandedResultId={expandedResultId}
                    onRowClick={(row) => setExpandedResultId((previous) => (previous === row.id ? null : row.id))}
                    fetchNextPage={() => {
                        // Scrolling on past a page that failed would retry it on every scroll event.
                        if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
                            void fetchNextPage();
                        }
                    }}
                    noDataLabel={
                        <OutreachResultsEmptyState
                            variant={getEmptyStateVariant({
                                status: request.status,
                                resultsCount: request.results_count,
                                hasFilters: getHasActiveFilters(urlState),
                                isError,
                            })}
                            errorMessage={errorMessage}
                        />
                    }
                />
            </div>

            <CreatorDetailsModal
                key={creatorDetailsAccountId}
                isOpen={creatorDetailsAccountId !== null}
                clientInfluencerAccountId={creatorDetailsAccountId ?? undefined}
                onClose={() => setCreatorDetailsAccountId(null)}
            />
        </section>
    );
};
